import { jsx as _jsx } from "react/jsx-runtime";
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, } from 'chart.js';
import { useTheme } from '../../contexts/ThemeContext';
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);
const OeeTrendChart = ({ data, title = 'OEE Trends', height = 300, showLegend = true, className = '', selectedDate = null, }) => {
    const { isDark } = useTheme();
    // Find the index of the selected date for highlighting
    const selectedIndex = selectedDate
        ? data.findIndex((d) => {
            const dataDate = new Date(d.timestamp);
            const selected = new Date(selectedDate);
            return dataDate.toDateString() === selected.toDateString();
        })
        : -1;
    const labels = data.map((d) => {
        // Use the formatted date if available, otherwise fallback to timestamp
        if (d.date) {
            return d.date;
        }
        return new Date(d.timestamp).toLocaleDateString();
    });
    // Create background colors array to highlight selected date
    const pointBackgroundColors = data.map((_, index) => index === selectedIndex ? 'rgb(239, 68, 68)' : 'rgb(59, 130, 246)');
    const chartData = {
        labels,
        datasets: [
            {
                label: 'OEE',
                data: data.map((d) => d.oee),
                borderColor: 'rgb(59, 130, 246)',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                borderWidth: 2,
                fill: true,
                tension: 0.4,
                pointBackgroundColor: pointBackgroundColors,
                pointBorderColor: pointBackgroundColors,
                pointRadius: data.map((_, index) => (index === selectedIndex ? 8 : 4)),
                pointHoverRadius: data.map((_, index) => index === selectedIndex ? 10 : 6),
            },
            {
                label: 'Availability',
                data: data.map((d) => d.availability),
                borderColor: 'rgb(34, 197, 94)',
                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                borderWidth: 2,
                fill: false,
                tension: 0.4,
            },
            {
                label: 'Performance',
                data: data.map((d) => d.performance),
                borderColor: 'rgb(251, 146, 60)',
                backgroundColor: 'rgba(251, 146, 60, 0.1)',
                borderWidth: 2,
                fill: false,
                tension: 0.4,
            },
            {
                label: 'Quality',
                data: data.map((d) => d.quality),
                borderColor: 'rgb(168, 85, 247)',
                backgroundColor: 'rgba(168, 85, 247, 0.1)',
                borderWidth: 2,
                fill: false,
                tension: 0.4,
            },
        ],
    };
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: showLegend,
                position: 'top',
                labels: {
                    color: isDark ? 'rgb(156, 163, 175)' : 'rgb(107, 114, 128)',
                },
            },
            title: {
                display: true,
                text: title,
                font: {
                    size: 16,
                    weight: 'bold',
                },
                color: isDark ? 'rgb(243, 244, 246)' : 'rgb(17, 24, 39)',
            },
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    label: function (context) {
                        var _a;
                        return `${context.dataset.label}: ${(_a = context.parsed.y) === null || _a === void 0 ? void 0 : _a.toFixed(1)}%`;
                    },
                },
            },
        },
        scales: {
            x: {
                display: true,
                title: {
                    display: true,
                    text: 'Date',
                    color: isDark ? 'rgb(156, 163, 175)' : 'rgb(107, 114, 128)',
                },
                ticks: {
                    color: isDark ? 'rgb(156, 163, 175)' : 'rgb(107, 114, 128)',
                },
                grid: {
                    color: isDark ? 'rgb(55, 65, 81)' : 'rgb(229, 231, 235)',
                },
            },
            y: {
                display: true,
                title: {
                    display: true,
                    text: 'Percentage (%)',
                    color: isDark ? 'rgb(156, 163, 175)' : 'rgb(107, 114, 128)',
                },
                min: 0,
                max: 100,
                ticks: {
                    color: isDark ? 'rgb(156, 163, 175)' : 'rgb(107, 114, 128)',
                    callback: function (value) {
                        return value + '%';
                    },
                },
                grid: {
                    color: isDark ? 'rgb(55, 65, 81)' : 'rgb(229, 231, 235)',
                },
            },
        },
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false,
        },
    };
    return (_jsx("div", { className: `bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 ${className}`, children: _jsx("div", { style: { height }, children: _jsx(Line, { data: chartData, options: options }) }) }));
};
export default OeeTrendChart;
